// 通用 Markdown 编辑器 —— textarea + 实时 HTML 预览。
//
// 三种视图模式（顶部切换按钮）：
//   - "编辑"  ：只显示 textarea，长文写作时不被 preview 抢空间
//   - "对照"  ：textarea | preview 左右分屏（桌面默认）
//   - "预览"  ：只渲染 HTML，用于最终核对客户站感官
//
// 移动端窄屏自动退回"编辑/预览"两态切换（CSS @media）。
//
// 安全性：渲染走 apps/admin/src/lib/markdown.ts 的 renderMarkdown（跟 marketing
// 同一份），它在内部 HTML-escape 所有字符再加回 markup 模式，stray <script>
// 永远不会执行。

import { useMemo, useState } from 'react'
import { Pencil, Eye, Columns } from 'lucide-react'
import { renderMarkdown } from '@/lib/markdown'
import { cn } from '@/lib/cn'

type Mode = 'edit' | 'split' | 'preview'

export function MarkdownPaneEditor({
  value,
  onChange,
  rows = 14,
  placeholder,
  /** 文档计数显示在 toolbar 右侧。可选。 */
  hint,
  /** toolbar 左边的标题。可选。 */
  label,
  /** 初始模式：写新内容时给 'edit'；查看从上游导入的预填用 'split'。 */
  initialMode = 'split',
  /** 编辑器主区域的固定高度（px）。给定后 textarea / preview 一起遵守，避免
   *  分屏时一侧短得离谱。不给则按 rows 自然增长。 */
  fixedHeight,
}: {
  value: string
  onChange: (v: string) => void
  rows?: number
  placeholder?: string
  hint?: string
  label?: string
  initialMode?: Mode
  fixedHeight?: number
}) {
  const [mode, setMode] = useState<Mode>(initialMode)
  // 渲染纯函数，memo 一下避免每次 keystroke 走 regex 一遍
  const html = useMemo(() => renderMarkdown(value), [value])

  const toolbarBtn = (m: Mode, Icon: typeof Pencil, text: string) => (
    <button
      type="button"
      onClick={() => setMode(m)}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
        mode === m ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70 text-muted-foreground',
      )}
    >
      <Icon className="w-3 h-3"/>{text}
    </button>
  )

  return (
    <div>
      {/* toolbar */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          {label && <span className="text-xs font-medium text-foreground">{label}</span>}
          <div className="flex items-center gap-0.5">
            {toolbarBtn('edit',    Pencil,  '编辑')}
            {toolbarBtn('split',   Columns, '对照')}
            {toolbarBtn('preview', Eye,     '预览')}
          </div>
        </div>
        {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
      </div>

      {/* panes */}
      <div
        className={cn(
          'rounded-md border border-border bg-card overflow-hidden',
          mode === 'split' ? 'grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border' : '',
        )}
        style={fixedHeight ? { height: fixedHeight } : undefined}
      >
        {(mode === 'edit' || mode === 'split') && (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            placeholder={placeholder}
            className={cn(
              'w-full bg-transparent px-3 py-2 text-xs font-mono leading-relaxed resize-none outline-none',
              fixedHeight ? 'h-full overflow-auto' : '',
            )}
            // split 模式下 textarea 不要扩到比 preview 更高
            style={fixedHeight ? undefined : { minHeight: `${rows * 1.4}em` }}
          />
        )}
        {(mode === 'split' || mode === 'preview') && (
          <div
            className={cn(
              'markdown-preview px-3 py-2 text-xs leading-relaxed overflow-auto',
              fixedHeight ? 'h-full' : '',
              // 分屏时给 preview 跟 textarea 同样的最小高，避免空内容时塌成 0
              !fixedHeight ? 'min-h-[14em]' : '',
            )}
            // SAFE: html comes from renderMarkdown which escapes all literals
            dangerouslySetInnerHTML={{ __html: html || '<p class="text-muted-foreground italic">（暂无内容预览）</p>' }}
          />
        )}
      </div>

      {/* Preview 区的浏览样式 —— scope 到 .markdown-preview 内，不影响 admin 其他地方。
          这里用 :global tailwind 写法不方便，直接 <style> 一段更直观。 */}
      <style>{`
        .markdown-preview h2 { font-size: 14px; font-weight: 600; margin: 0.6em 0 0.3em; color: var(--foreground); }
        .markdown-preview h3 { font-size: 13px; font-weight: 600; margin: 0.5em 0 0.25em; color: var(--foreground); }
        .markdown-preview h4 { font-size: 12.5px; font-weight: 600; margin: 0.4em 0 0.2em; color: var(--foreground); }
        .markdown-preview p  { margin: 0.4em 0; }
        .markdown-preview ul,
        .markdown-preview ol { margin: 0.3em 0; padding-left: 1.4em; }
        .markdown-preview li { margin: 0.15em 0; }
        .markdown-preview code { background: var(--muted); padding: 0.05em 0.3em; border-radius: 3px; font-size: 0.9em; }
        .markdown-preview strong { font-weight: 600; }
        .markdown-preview em { font-style: italic; }
        .markdown-preview a { color: var(--primary); text-decoration: underline; }
        .markdown-preview hr { border: 0; border-top: 1px solid var(--border); margin: 0.8em 0; }
        .markdown-preview img { max-width: 100%; height: auto; border-radius: 4px; margin: 0.4em 0; }
      `}</style>
    </div>
  )
}
